import type { PlanCode } from '../types';

/**
 * The website's pricing buttons link to `{lobby}/billing?plan=starter|pro|team`. The chosen plan rides in
 * the OIDC `state` through sign-in (and a new user's region selection), never in browser storage, and is
 * consumed before the onboarding wizard to open Checkout.
 */
export const PLAN_INTENT_PATH = '/billing';

/** The plans the website can ask for. */
export type PlanIntent = Extract<PlanCode, 'starter' | 'pro'>;

/** What rides in the OIDC `state` and the router's location state. */
export interface PlanIntentState {
  planIntent: PlanIntent;
}

const PLAN_INTENTS: readonly PlanIntent[] = ['starter', 'pro'];

/** Validates a `plan` value from the URL. Anything unexpected is ignored rather than trusted. */
export function parsePlanIntent(value: unknown): PlanIntent | null {
  if (typeof value !== 'string') return null;
  const normalised = value.trim().toLowerCase();
  return PLAN_INTENTS.find((plan) => plan === normalised) ?? null;
}

/** The plan asked for by a `/billing?plan=` URL, or null anywhere else. */
export function planIntentFromLocation(pathname: string, search: string): PlanIntent | null {
  if (pathname.replace(/\/+$/, '').toLowerCase() !== PLAN_INTENT_PATH) return null;
  return parsePlanIntent(new URLSearchParams(search).get('plan'));
}

/** The OIDC `state` to sign in with, or undefined when there is no intent to carry. */
export function toPlanIntentState(planIntent: PlanIntent | null): PlanIntentState | undefined {
  return planIntent ? { planIntent } : undefined;
}

/** Reads the intent back from the OIDC `state` or a router location state. */
export function readPlanIntent(state: unknown): PlanIntent | null {
  if (state === null || typeof state !== 'object') return null;
  return parsePlanIntent((state as { planIntent?: unknown }).planIntent);
}
